import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Play, Download, CheckCircle, Layers, Filter, X,
         Settings, Calendar, Clock, Save } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const fmt = (n) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(2)}L`
    : `₹${Math.round(n).toLocaleString("en-IN")}`;

const Tip = ({ active, payload, label }) => {
  const { T } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:8,
      padding:"10px 14px", fontFamily:"Outfit,sans-serif", fontSize:13 }}>
      <p style={{ color:T.muted, margin:"0 0 6px", fontSize:11 }}>{label}</p>
      {payload.map((p,i) =>
        <p key={i} style={{ color:p.color, margin:"3px 0", fontWeight:600 }}>
          {p.name}: <span style={{ color:T.text }}>{fmt(p.value)}</span>
        </p>)}
    </div>
  );
};

// ── Client-side period + working-days calculator ───────────────────────
// Mirrors the backend _calc_period() logic so the UI can show a live
// preview without a round-trip.
function calcPeriod(month, periodStartDay, satWorking) {
  const [year, mon] = month.split("-").map(Number);
  let start, end;

  const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

  if (periodStartDay <= 1) {
    start = new Date(year, mon - 1, 1);
    end   = new Date(year, mon - 1, daysInMonth(year, mon));
  } else {
    const prevMon  = mon === 1 ? 12    : mon - 1;
    const prevYear = mon === 1 ? year - 1 : year;
    const maxPrev  = daysInMonth(prevYear, prevMon);
    start = new Date(prevYear, prevMon - 1, Math.min(periodStartDay, maxPrev));
    end   = new Date(year, mon - 1, Math.min(periodStartDay - 1, daysInMonth(year, mon)));
  }

  let workingDays = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const wd = cur.getDay(); // 0=Sun, 6=Sat
    if (wd !== 0 && (wd !== 6 || satWorking)) workingDays++;
    cur.setDate(cur.getDate() + 1);
  }

  const fmt2 = (d) => d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  return { start: fmt2(start), end: fmt2(end), workingDays };
}

// ── Ordinal helper (1→"1st", 26→"26th") ──────────────────────────────
function ordinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export default function PayrollDashboard() {
  const { T } = useTheme();
  const STATUS_COLOR = { Draft:T.muted, Processing:T.cyan, Completed:T.amber, Paid:T.green };
  const navigate = useNavigate();

  const [stats,       setStats]       = useState({});
  const [runs,        setRuns]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [running,     setRunning]     = useState(false);
  const [selMonth,    setSelMonth]    = useState(new Date().toISOString().slice(0,7));
  const [filterMonth, setFilterMonth] = useState("");

  // Payroll config state
  const [cfg,         setCfg]         = useState({ period_start_day:1, saturday_working:false });
  const [cfgModal,    setCfgModal]    = useState(false);
  const [cfgForm,     setCfgForm]     = useState({ period_start_day:1, saturday_working:false });
  const [savingCfg,   setSavingCfg]   = useState(false);

  // Working-days info for selected month (live-computed from cfg)
  const periodInfo = calcPeriod(selMonth, cfg.period_start_day, cfg.saturday_working);
  const formPeriodInfo = calcPeriod(selMonth, cfgForm.period_start_day, cfgForm.saturday_working);

  // ── Fetch data ──────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const [sRes, rRes, cRes] = await Promise.all([
        axios.get(APIConstant.payrollStats, ax()),
        axios.get(APIConstant.payrollRuns,  ax()),
        axios.get(`${APIConstant.payrollStats.replace("stats","config") || "/api/payroll-config/"}`, ax())
          .catch(() => ({ data: { period_start_day:1, saturday_working:false } })),
      ]);
      setStats(sRes.data || {});
      setRuns(rRes.data  || []);
      const c = cRes.data || {};
      setCfg({ period_start_day: c.period_start_day || 1, saturday_working: !!c.saturday_working });
      setCfgForm({ period_start_day: c.period_start_day || 1, saturday_working: !!c.saturday_working });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Filtered runs + stats ───────────────────────────────────────────
  const filteredRuns = filterMonth ? runs.filter(r => r.month === filterMonth) : runs;
  const filteredStats = (() => {
    if (!filterMonth || filteredRuns.length === 0)
      return { gross:stats.latest_gross||0, net:stats.latest_net||0,
               employees:stats.latest_employees||0, totalRuns:stats.total_runs||0, label:"Latest" };
    const gross     = filteredRuns.reduce((s,r) => s + (r.total_gross     || 0), 0);
    const net       = filteredRuns.reduce((s,r) => s + (r.total_net       || 0), 0);
    const employees = filteredRuns.reduce((s,r) => s + (r.total_employees || 0), 0);
    return { gross, net, employees, totalRuns:filteredRuns.length, label:filterMonth };
  })();

  // ── Actions ─────────────────────────────────────────────────────────
  const runPayroll = async () => {
    if (!selMonth) return;
    setRunning(true);
    try {
      const rRes = await axios.post(`${APIConstant.payrollRuns}?month=${selMonth}`, {}, ax());
      const runId = rRes.data.id;
      await axios.post(`${APIConstant.payrollRuns}/${runId}/process`, {}, ax());
      fetchAll();
      alert(`✅ Payroll processed for ${selMonth}\nPeriod: ${periodInfo.start} – ${periodInfo.end}\nWorking days: ${periodInfo.workingDays}`);
    } catch(e) {
      alert(e.response?.data?.detail || "Error processing payroll");
    } finally { setRunning(false); }
  };

  const markPaid = async (id) => {
    try { await axios.put(`${APIConstant.payrollRuns}/${id}/mark-paid`, {}, ax()); fetchAll(); }
    catch(e) { alert(e.response?.data?.detail || "Error"); }
  };

  const deleteRun = async (id) => {
    if (!window.confirm("Delete this payroll run?")) return;
    try { await axios.delete(`${APIConstant.payrollRuns}/${id}`, ax()); fetchAll(); }
    catch(e) { alert(e.response?.data?.detail || "Error"); }
  };

  const saveConfig = async () => {
    if (cfgForm.period_start_day < 1 || cfgForm.period_start_day > 28)
      return alert("Period start day must be between 1 and 28");
    setSavingCfg(true);
    try {
      await axios.put(
        `${APIConstant.payrollStats.replace("stats","config") || "/api/payroll-config/"}`,
        { period_start_day: cfgForm.period_start_day, saturday_working: cfgForm.saturday_working },
        ax()
      );
      setCfg({ ...cfgForm });
      setCfgModal(false);
      alert("✅ Payroll configuration saved");
    } catch(e) { alert(e.response?.data?.detail || "Error saving config"); }
    finally { setSavingCfg(false); }
  };

  // ── Period label ─────────────────────────────────────────────────────
  const periodLabel = cfg.period_start_day <= 1
    ? "1st to last day (calendar month)"
    : `${ordinal(cfg.period_start_day)} to ${ordinal(cfg.period_start_day - 1)} (cross-month)`;

  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:6,
                textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:"Outfit,sans-serif" };

  return (
    <div className={styles.pageRoot}>

      {/* ── Page header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>COMPENSATION</div>
          <h2>Payroll Dashboard</h2>
          <p>Monthly payroll runs, salary disbursements and analytics</p>
        </div>
        <div className={styles.headerRight}>

          {/* Month filter */}
          <div style={{ display:"flex", alignItems:"center", gap:6,
            background:T.inp_bg, border:`1px solid ${filterMonth?T.cyan:T.border}`,
            borderRadius:8, padding:"5px 10px" }}>
            <Filter size={13} style={{ color:filterMonth?T.cyan:T.dim, flexShrink:0 }}/>
            <input type="month" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}
              title="Filter history by month"
              style={{ background:"transparent", border:"none", color:filterMonth?T.text:T.muted,
                fontSize:13, outline:"none", fontFamily:"Outfit,sans-serif",
                width:filterMonth?130:120, cursor:"pointer" }}/>
            {filterMonth && (
              <button onClick={()=>setFilterMonth("")}
                style={{ background:"none", border:"none", cursor:"pointer",
                  color:T.dim, display:"flex", alignItems:"center", padding:0 }}>
                <X size={12}/>
              </button>
            )}
          </div>

          {/* Payroll settings */}
          <button className={styles.btnSecondary} onClick={()=>{ setCfgForm({...cfg}); setCfgModal(true); }}>
            <Settings size={14}/> Payroll Settings
          </button>
          <button className={styles.btnSecondary} onClick={()=>navigate("/admin/hrms/payslips")}>
            <Download size={14}/> Payslips
          </button>
          <button className={styles.btnSecondary} onClick={()=>navigate("/admin/hrms/salary")}>
            <Layers size={14}/> Salary Structures
          </button>
        </div>
      </div>

      {/* ── Current config banner ── */}
      <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px",
          background:`${T.cyan}0D`, border:`1px solid ${T.cyan}20`, borderRadius:8,
          fontSize:12.5, color:T.muted }}>
          <Calendar size={13} style={{ color:T.cyan }}/>
          <span>Period: <strong style={{ color:T.cyan }}>{periodLabel}</strong></span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px",
          background:`${T.green}0D`, border:`1px solid ${T.green}20`, borderRadius:8,
          fontSize:12.5, color:T.muted }}>
          <Clock size={13} style={{ color:T.green }}/>
          <span>Saturday: <strong style={{ color:T.green }}>{cfg.saturday_working ? "Working day" : "Off"}</strong></span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px",
          background:`${T.purple}0D`, border:`1px solid ${T.purple}20`, borderRadius:8,
          fontSize:12.5, color:T.muted }}>
          <span>Working days for <strong style={{ color:T.purple }}>{selMonth}</strong>:&nbsp;
            <strong style={{ color:T.purple, fontFamily:"JetBrains Mono,monospace" }}>
              {periodInfo.workingDays} days
            </strong>
            &nbsp;<span style={{ fontSize:11, color:T.dim }}>({periodInfo.start} – {periodInfo.end})</span>
          </span>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:`${filteredStats.label} Gross`,  val:fmt(filteredStats.gross),     color:"amber"  },
          { label:`${filteredStats.label} Net`,     val:fmt(filteredStats.net),       color:"green"  },
          { label:"Employees Paid",                 val:filteredStats.employees,      color:"cyan"   },
          { label:filterMonth?"Runs (filtered)":"Total Runs", val:filteredStats.totalRuns, color:"purple" },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal} style={{ fontSize:22 }}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={`${styles.contentRow} ${styles.col2}`}>
        {/* Trend chart */}
        <div className={styles.card}>
          <div className={styles.cardHeader}><h3>Monthly Payroll Trend</h3><span className={styles.cardMeta}>Last 6 months</span></div>
          <div className={styles.cardBody}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={stats.trend || []} margin={{ top:5, right:5, left:0, bottom:0 }}>
                <defs>
                  <linearGradient id="gg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={T.amber} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={T.amber} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={T.green} stopOpacity={0.2}/>
                    <stop offset="95%" stopColor={T.green} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.toggle_bg}/>
                <XAxis dataKey="month" tick={{ fill:T.muted, fontSize:11 }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fill:T.muted, fontSize:10 }} axisLine={false} tickLine={false}
                  tickFormatter={v=>`₹${(v/100000).toFixed(0)}L`}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="gross" name="Gross" stroke={T.amber} strokeWidth={2} fill="url(#gg)" dot={false}/>
                <Area type="monotone" dataKey="net"   name="Net"   stroke={T.green} strokeWidth={2} fill="url(#gn)" dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Run payroll */}
        <div className={styles.card}>
          <div className={styles.cardHeader}><h3>Run Payroll</h3></div>
          <div className={styles.cardBody} style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={lbl}>Select Month</label>
              <input type="month" value={selMonth} onChange={e=>setSelMonth(e.target.value)}
                style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.cyan}26`,
                  borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
                  outline:"none", fontFamily:"Outfit,sans-serif" }}/>
            </div>

            {/* Period preview — updates live with selMonth */}
            <div style={{ background:T.toggle_bg, borderRadius:9, padding:"10px 14px",
              display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              {[
                { label:"Period Start",   val:periodInfo.start },
                { label:"Period End",     val:periodInfo.end   },
                { label:"Working Days",   val:`${periodInfo.workingDays} days`,
                  highlight: true },
              ].map(({ label, val, highlight }) => (
                <div key={label} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9.5, color:T.dim, textTransform:"uppercase",
                    letterSpacing:"0.08em", marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:12.5, fontFamily:"JetBrains Mono,monospace",
                    fontWeight:700, color: highlight ? T.green : T.text }}>{val}</div>
                </div>
              ))}
            </div>

            <div style={{ background:`${T.cyan}0D`, border:`1px solid ${T.cyan}1A`,
              borderRadius:9, padding:"11px 14px", fontSize:12.5, color:T.muted, lineHeight:1.7 }}>
              ℹ️ Running payroll will:<br/>
              • Calculate gross salary for all active employees<br/>
              • Apply PF, ESI, PT and TDS deductions<br/>
              • Calculate LOP based on {periodInfo.workingDays} working days<br/>
              • Generate individual payslips
            </div>
            <button className={styles.btnPrimary} onClick={runPayroll} disabled={running}
              style={{ justifyContent:"center" }}>
              {running ? "Processing…" : <><Play size={14}/> Run Payroll for {selMonth}</>}
            </button>
            <button className={styles.btnSecondary} onClick={()=>navigate("/admin/hrms/payroll/process")}
              style={{ justifyContent:"center" }}>
              View Detailed Process
            </button>
          </div>
        </div>
      </div>

      {/* ── Run history ── */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Payroll Run History</h3>
          <span className={styles.cardMeta}>
            {filterMonth
              ? `${filteredRuns.length} run${filteredRuns.length!==1?"s":""} · ${filterMonth}`
              : `${runs.length} runs`}
          </span>
        </div>
        {loading ? <div className={styles.loader}><div className={styles.spinner}/>Loading…</div> : (
          <div className={styles.tableWrap}>
            <table className={styles.darkTable}>
              <thead>
                <tr><th>Month</th><th>Employees</th><th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Status</th><th>Processed</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredRuns.length === 0 ? (
                  <tr className={styles.emptyRow}>
                    <td colSpan={8}>
                      {filterMonth
                        ? `No run found for ${filterMonth} — clear filter to see all`
                        : `No payroll runs yet — click "Run Payroll" above`}
                    </td>
                  </tr>
                ) : filteredRuns.map(r => (
                  <tr key={r.id}>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", fontWeight:700 }}>{r.month}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.cyan }}>{r.total_employees}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber }}>{fmt(r.total_gross)}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.red }}>{fmt(r.total_deductions)}</span></td>
                    <td><span style={{ fontFamily:"JetBrains Mono,monospace", color:T.green, fontWeight:700 }}>{fmt(r.total_net)}</span></td>
                    <td>
                      <span style={{ fontSize:11.5, padding:"3px 9px", borderRadius:20, fontWeight:600,
                        background:`${STATUS_COLOR[r.status]}20`, color:STATUS_COLOR[r.status] }}>
                        {r.status}
                      </span>
                    </td>
                    <td className={styles.dim}>{r.processed_at ? new Date(r.processed_at).toLocaleDateString("en-IN") : "—"}</td>
                    <td>
                      <div style={{ display:"flex", gap:6 }}>
                        <button className={styles.btnSm} onClick={()=>navigate(`/admin/hrms/payslips?run_id=${r.id}`)}>
                          <Download size={12}/> Payslips
                        </button>
                        {r.status === "Completed" && (
                          <button className={styles.btnSm} style={{ color:T.green, borderColor:`${T.green}40` }}
                            onClick={()=>markPaid(r.id)}>
                            <CheckCircle size={12}/> Mark Paid
                          </button>
                        )}
                        {r.status !== "Paid" && (
                          <button className={styles.btnSm} style={{ color:T.red, borderColor:`${T.red}40` }}
                            onClick={()=>deleteRun(r.id)}>✕</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          PAYROLL SETTINGS MODAL
      ══════════════════════════════════════════════════════════════ */}
      {cfgModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:9999,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"Outfit,sans-serif", padding:16 }}
          onClick={e=>{ if(e.target===e.currentTarget && !savingCfg) setCfgModal(false); }}>

          <div style={{ background:T.card, border:`1px solid ${T.cyan}40`, borderRadius:14,
            width:500, overflow:"hidden", boxShadow:`0 20px 60px rgba(0,0,0,0.5)` }}>

            {/* Header */}
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`,
              background:`${T.cyan}08`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:10, color:T.cyan, fontWeight:700, textTransform:"uppercase",
                  letterSpacing:"0.1em", marginBottom:4 }}>COMPENSATION SETTINGS</div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:T.text }}>Payroll Configuration</h3>
              </div>
              <button onClick={()=>setCfgModal(false)} disabled={savingCfg}
                style={{ background:T.toggle_bg, border:"none", borderRadius:"50%", width:32, height:32,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer", color:T.muted }}>
                <X size={14}/>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding:24, display:"flex", flexDirection:"column", gap:22 }}>

              {/* ── Section 1: Payroll period ── */}
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:4 }}>
                  📅 Payroll Period
                </div>
                <div style={{ fontSize:12, color:T.muted, marginBottom:14 }}>
                  Define when the payroll cycle starts and ends each month.
                </div>

                {/* Calendar month option */}
                <label style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px",
                  borderRadius:9, cursor:"pointer", marginBottom:8,
                  border:`1px solid ${cfgForm.period_start_day===1?T.cyan:T.border}`,
                  background:cfgForm.period_start_day===1?`${T.cyan}0D`:T.toggle_bg }}>
                  <input type="radio" name="period" checked={cfgForm.period_start_day===1}
                    onChange={()=>setCfgForm(p=>({...p,period_start_day:1}))}
                    style={{ marginTop:2, accentColor:T.cyan, flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text }}>
                      Calendar Month <span style={{ fontSize:11, color:T.cyan, fontWeight:700 }}>1st → last day</span>
                    </div>
                    <div style={{ fontSize:11.5, color:T.muted, marginTop:3 }}>
                      e.g. June payroll = Jun 1 – Jun 30 · ~22 or 26 working days
                    </div>
                  </div>
                </label>

                {/* Custom start day option */}
                <label style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 14px",
                  borderRadius:9, cursor:"pointer",
                  border:`1px solid ${cfgForm.period_start_day>1?T.amber:T.border}`,
                  background:cfgForm.period_start_day>1?`${T.amber}0D`:T.toggle_bg }}>
                  <input type="radio" name="period" checked={cfgForm.period_start_day>1}
                    onChange={()=>setCfgForm(p=>({...p,period_start_day:26}))}
                    style={{ marginTop:2, accentColor:T.amber, flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:8 }}>
                      Custom Cross-Month Period
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:12.5, color:T.muted, whiteSpace:"nowrap" }}>Period starts on the</span>
                      <input
                        type="number" min={2} max={28}
                        value={cfgForm.period_start_day > 1 ? cfgForm.period_start_day : 26}
                        disabled={cfgForm.period_start_day <= 1}
                        onChange={e => {
                          const v = Math.max(2, Math.min(28, parseInt(e.target.value)||26));
                          setCfgForm(p=>({...p, period_start_day:v}));
                        }}
                        style={{ width:64, background:T.inp_bg,
                          border:`1px solid ${cfgForm.period_start_day>1?T.amber:T.border}`,
                          borderRadius:7, color:cfgForm.period_start_day>1?T.text:T.dim,
                          fontSize:14, fontFamily:"JetBrains Mono,monospace", fontWeight:700,
                          padding:"5px 8px", outline:"none", textAlign:"center" }}/>
                      <span style={{ fontSize:12.5, color:T.muted }}>of the previous month</span>
                    </div>
                    {cfgForm.period_start_day > 1 && (
                      <div style={{ fontSize:11.5, color:T.amber, marginTop:6 }}>
                        e.g. June payroll = May {cfgForm.period_start_day} – Jun {cfgForm.period_start_day - 1}
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* ── Section 2: Saturday ── */}
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:4 }}>
                  🗓 Saturday Policy
                </div>
                <div style={{ fontSize:12, color:T.muted, marginBottom:14 }}>
                  Determines the total working days used for LOP and salary calculation.
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[
                    { val:false, label:"Saturday Off", sub:"Mon – Fri only", days:"~22 working days", color:T.green },
                    { val:true,  label:"Saturday Working", sub:"Mon – Sat", days:"~26 working days", color:T.amber },
                  ].map(({ val, label, sub, days, color }) => (
                    <label key={String(val)}
                      style={{ display:"flex", flexDirection:"column", gap:4, padding:"12px 14px",
                        borderRadius:9, cursor:"pointer",
                        border:`1px solid ${cfgForm.saturday_working===val?color:T.border}`,
                        background:cfgForm.saturday_working===val?`${color}0D`:T.toggle_bg }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="radio" name="saturday" checked={cfgForm.saturday_working===val}
                          onChange={()=>setCfgForm(p=>({...p,saturday_working:val}))}
                          style={{ accentColor:color }}/>
                        <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{label}</span>
                      </div>
                      <div style={{ fontSize:11.5, color:T.muted, paddingLeft:20 }}>{sub}</div>
                      <div style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace",
                        fontWeight:700, color, paddingLeft:20 }}>{days}</div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ── Live preview ── */}
              <div style={{ background:T.toggle_bg, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase",
                  letterSpacing:"0.08em", marginBottom:10 }}>Preview for {selMonth}</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                  {[
                    { label:"Period Start",   val:formPeriodInfo.start },
                    { label:"Period End",     val:formPeriodInfo.end   },
                    { label:"Working Days",   val:`${formPeriodInfo.workingDays} days`, accent:true },
                  ].map(({ label, val, accent }) => (
                    <div key={label} style={{ textAlign:"center", padding:"8px 0" }}>
                      <div style={{ fontSize:9.5, color:T.dim, textTransform:"uppercase",
                        letterSpacing:"0.07em", marginBottom:4 }}>{label}</div>
                      <div style={{ fontSize:13, fontFamily:"JetBrains Mono,monospace",
                        fontWeight:700, color:accent?T.green:T.text }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding:"14px 24px", borderTop:`1px solid ${T.border}`,
              display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={()=>setCfgModal(false)} disabled={savingCfg}
                style={{ padding:"9px 18px", borderRadius:8, border:`1px solid ${T.border}`,
                  background:"none", color:T.muted, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:600 }}>
                Cancel
              </button>
              <button onClick={saveConfig} disabled={savingCfg}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 22px",
                  borderRadius:8, border:"none", background:T.cyan, color:"#000",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
                  cursor:savingCfg?"not-allowed":"pointer", opacity:savingCfg?0.7:1 }}>
                <Save size={14}/>{savingCfg?"Saving…":"Save Configuration"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}